// Persistence for the embedded CPLEX Modeler chat.
//
// The backend (agent_console_conversation) is the source of truth for the
// transcript; localStorage holds ONLY the conversation id as a lightweight
// pointer, and on mount the panel GETs the conversation's turns and renders the
// server's copy. No transcript in storage — only the pointer, so a cleared cache
// or a second browser can't blank or fork the thread.
//
// The pointer now rides on the SHARED conversation-history store
// (components/agentConsole/conversationHistory.makeActiveConvStore) so the CPLEX
// Modeler uses the same persistence primitive as every other Agent SDK door
// instead of its own bespoke localStorage logic. The embedded panel is a single
// admin surface (not a per-user portal), so it uses the store's default bucket.
//
// SDK-free + dependency-light so it unit-tests in jsdom.

import type { AgentFileData } from '../agentConsole/agentFileCards';
import { makeActiveConvStore } from '../agentConsole/conversationHistory';
import {
  buildTranscriptTimeline,
  type AssistantBlock,
  type ConversationDetail,
  type UsageEvent,
} from '../../services/agentConsole';

export interface UserMsg {
  role: 'user';
  text: string;
}
export interface AssistantMsg {
  role: 'assistant';
  blocks: AssistantBlock[];
  usage: UsageEvent | null;
  /** Files the agent attached this turn (attach_file) — one card each. */
  agentFiles?: AgentFileData[];
}
export type ChatMsg = UserMsg | AssistantMsg;

// The shared per-user active-conversation store. The embedded CPLEX panel is a
// single admin surface, so it uses the default (non-per-user) bucket.
const _store = makeActiveConvStore('cm_cplex_convid_');

/** The persisted conversation-id pointer, or null when absent. */
export function loadModelerConvId(): string | null {
  return _store.read(undefined);
}

/** Persist the active conversation id as the only stored state. */
export function saveModelerConvId(convId: string): void {
  if (convId) _store.write(undefined, convId);
}

/** Forget the pointer (the Reset button calls this). */
export function clearModelerConvId(): void {
  _store.write(undefined, null);
}

/**
 * Convert a fetched conversation detail (the backend's turns + tool-call audit)
 * into the panel's ChatMsg[] — the SAME shape the live SSE stream builds — so a
 * rehydrated thread renders identically to one you just typed. Reuses
 * buildTranscriptTimeline (the AgentConsolePage transcript builder) rather than
 * re-deriving block/tool pairing.
 */
export function chatMsgsFromDetail(detail: ConversationDetail): ChatMsg[] {
  return buildTranscriptTimeline(detail.messages, detail.tool_calls).map(
    (item): ChatMsg => {
      if (item.kind === 'user') {
        return { role: 'user', text: item.message.content };
      }
      const m = item.message;
      const hasUsage =
        m.input_tokens != null ||
        m.output_tokens != null ||
        m.cost_usd != null ||
        m.stop_reason != null;
      const usage: UsageEvent | null = hasUsage
        ? {
            type: 'result',
            stop_reason: m.stop_reason,
            input_tokens: m.input_tokens,
            output_tokens: m.output_tokens,
            cache_creation_input_tokens: m.cache_creation_input_tokens,
            cache_read_input_tokens: m.cache_read_input_tokens,
            cost_usd: m.cost_usd,
          }
        : null;
      return {
        role: 'assistant',
        blocks: item.blocks,
        usage,
        // Agent-file cards come pre-lifted by buildTranscriptTimeline.
        agentFiles: item.agentFiles,
      };
    },
  );
}
